/**
 * Clemtex.CategoryLinks.Rules - pure logic (no DOM, no network). Unit-tested in
 * Node by test/run-tests.js. Mirrors the server-side rules in
 * Commerce Categories/bb_cx_publish_lib.js (placement, bar summary).
 *
 * Option A (approved 2026-10-11):
 *   - one link            -> the bar only
 *   - <= one page (48)    -> bar under the heading + tile strip after the grid
 *   - more than one page  -> bar; "+N more related" expands the tiles under the bar
 *     (infinite scroll keeps pushing a bottom strip out of reach)
 */
define('Clemtex.CategoryLinks.Rules', [], function () {
    'use strict';

    var DEFAULT_PAGE_SIZE = 48;
    var THUMB_SUFFIX = '&resizeid=2&resizeh=600&resizew=600';   // site "thumbnail" image preset

    function placement(linkCount, itemCount, pageSize) {
        if (!linkCount) { return 'none'; }
        if (linkCount === 1) { return 'bar'; }
        return (itemCount || 0) <= (pageSize || DEFAULT_PAGE_SIZE) ? 'bar+bottom' : 'bar+inline';
    }

    /** First relation group's first `max` links + how many more exist. */
    function barFor(entries, max) {
        if (!entries || !entries.length) { return null; }
        var rel = entries[0].rel;
        var group = [];
        for (var i = 0; i < entries.length; i++) { if (entries[i].rel === rel) { group.push(entries[i]); } }
        var shown = group.slice(0, max || 3);
        return { rel: rel, links: shown, more: entries.length - shown.length };
    }

    /** Consecutive entries with the same relation form a group (the publisher already sorted them). */
    function groupsFor(entries, relations) {
        var labels = {};
        (relations || []).forEach(function (r) { labels[r.key] = r.label; });
        var out = [];
        (entries || []).forEach(function (e) {
            var g = out.length ? out[out.length - 1] : null;
            if (!g || g.rel !== e.rel) {
                g = { rel: e.rel, label: labels[e.rel] || e.rel, sub: '', entries: [] };
                out.push(g);
            }
            if (!g.sub && e.sub) { g.sub = e.sub; }
            g.entries.push(e);
        });
        return out;
    }

    /** Entries for a category, resolved against the published category index. */
    function entriesFor(payload, catId) {
        if (!payload || payload.enabled === false || !payload.links) { return []; }
        var raw = payload.links[String(catId)] || [];
        var cats = payload.cats || {};
        var out = [];
        raw.forEach(function (l) {
            var c = cats[l.to];
            if (!c || !c.u) { return; }
            out.push({ rel: l.rel, to: String(l.to), name: l.label || c.n, url: c.u, img: thumb(c.t), sub: l.sub || '' });
        });
        return out;
    }

    /** "Parts for this tool" for a product: first item link whose target is published. */
    function itemLinkFor(payload, itemId) {
        if (!payload || payload.enabled === false || !payload.itemLinks) { return null; }
        var list = payload.itemLinks[String(itemId)] || [];
        var cats = payload.cats || {};
        for (var i = 0; i < list.length; i++) {
            var c = cats[list[i].to];
            if (c && c.u) {
                return { to: String(list[i].to), name: c.n, url: c.u, img: thumb(c.t), featured: (list[i].featured || []).slice(0, 4) };
            }
        }
        return null;
    }

    /** The question shown at the start of the bar for a relation. */
    function questionFor(payload, rel) {
        return (payload && payload.questions && payload.questions[rel]) || 'Related:';
    }

    /**
     * Brand pages (ClemtexBrands: /clemco/blast/equipment) re-use the category's
     * PLP under a brand prefix; v1 shows no cross-links there (decision #4).
     */
    function isCategoryPath(pathname, fullurl) {
        if (!fullurl) { return false; }
        var p = String(pathname || '').replace(/\/+$/, '').toLowerCase();
        var u = String(fullurl).replace(/\/+$/, '').toLowerCase();
        return p === u;
    }

    /** Sized thumbnail for a File Cabinet media URL (as product tiles use). */
    function thumb(url) {
        if (!url) { return ''; }
        if (/media\.nl/.test(url) && !/resizeid=/.test(url)) { return url + THUMB_SUFFIX; }
        return url;
    }

    /** Cache key that changes every hour: edits reach shoppers within an hour, and the CDN keeps one copy per hour. */
    function hourBucket(now) {
        return Math.floor((now || Date.now()) / 3600000);
    }

    function itemsApiUrl(params, query) {
        var q = ['c=' + encodeURIComponent(params.c), 'n=' + encodeURIComponent(params.n), 'fieldset=search'];
        Object.keys(query).forEach(function (k) { q.push(k + '=' + encodeURIComponent(query[k])); });
        return '/api/items?' + q.join('&');
    }

    /** Featured-part card data from an Item Search API item. */
    function cardFromItem(item) {
        if (!item) { return null; }
        var url = item.urlcomponent ? '/' + item.urlcomponent : '/product/' + item.internalid;
        var img = firstImage(item.itemimages_detail);
        if (!img && item.urlcomponent) { img = '/img/Item%20Images/' + encodeURIComponent(item.urlcomponent) + '_01.jpg'; }
        return {
            id: String(item.internalid),
            sku: item.itemid || '',
            name: item.storedisplayname2 || item.displayname || item.itemid || '',
            url: url,
            img: img,
            price: item.ispricevisible === false ? '' : (item.onlinecustomerprice_formatted || '')
        };
    }

    function firstImage(detail) {
        if (!detail || typeof detail !== 'object') { return ''; }
        if (detail.url && typeof detail.url === 'string') { return detail.url; }
        if (Array.isArray(detail.urls) && detail.urls.length && detail.urls[0].url) { return detail.urls[0].url; }
        var keys = Object.keys(detail);
        for (var i = 0; i < keys.length; i++) {
            var found = firstImage(detail[keys[i]]);
            if (found) { return found; }
        }
        return '';
    }

    function esc(s) {
        return String(s === null || s === undefined ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    return {
        DEFAULT_PAGE_SIZE: DEFAULT_PAGE_SIZE,
        placement: placement,
        barFor: barFor,
        groupsFor: groupsFor,
        entriesFor: entriesFor,
        itemLinkFor: itemLinkFor,
        questionFor: questionFor,
        isCategoryPath: isCategoryPath,
        thumb: thumb,
        hourBucket: hourBucket,
        itemsApiUrl: itemsApiUrl,
        cardFromItem: cardFromItem,
        esc: esc
    };
});
